import { motion } from "framer-motion";
import { ArrowRight, Apple, Play } from "lucide-react";
import PhoneMockup from "@/components/PhoneMockup";
import { MaskLine } from "@/lib/motion";
import { trackAndroid, trackIos } from "@/lib/stats";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "@/lib/links";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Hero() {
  return (
    <section id="hero" data-testid="hero-section" className="relative overflow-hidden border-b border-white/10 pt-36 pb-20 lg:pt-40 lg:pb-24">
      <div className="pointer-events-none absolute inset-0 -z-10 spotlight opacity-60" />

      <div className="mx-auto grid grid-cols-1 items-center gap-16 px-5 lg:grid-cols-[1.1fr_0.9fr] lg:px-8 max-w-6xl">
        <div>
          <div className="mb-6 flex flex-wrap items-center gap-3">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            data-testid="hero-launch-badge"
            className="inline-flex items-center gap-2.5 rounded-full border border-[#22C55E]/35 bg-[#22C55E]/12 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#22C55E]"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#22C55E] opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#22C55E]" />
            </span>
            Offiziell für iOS & Android verfügbar
          </motion.div>
          </div>

          <h1 className="font-display text-5xl font-bold leading-[0.95] tracking-tighter text-white sm:text-6xl">
            <MaskLine delay={0.05}>Plane Festivals</MaskLine>
            <MaskLine
              delay={0.18}
              className="text-gradient -mx-[0.06em] px-[0.06em] pb-[0.16em]"
            >
              gemeinsam.
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.45, duration: 0.8 }}
            className="mt-6 max-w-lg text-base leading-relaxed text-white/60 lg:text-lg"
          >
            Alles an einem Ort – Gruppen, Aufgaben, Fahrgemeinschaften, Mitbringlisten
            und Gruppenkasse.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55, duration: 0.8 }}
            className="mt-4 max-w-lg text-sm leading-relaxed text-white/55"
          >
            <span className="text-[#22C55E]">FestivalPlan ist seit Oktober 2026 offiziell für Android und iOS verfügbar! 🎉</span>{" "}
            Lade die App kostenlos im Google Play Store oder im Apple App Store und plane dein nächstes
            Festival gemeinsam mit deiner Crew.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.62, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackIos}
              data-testid="hero-cta-appstore"
              className="btn-primary group flex items-center gap-2 px-7 py-3.5 text-sm font-medium"
            >
              <Apple className="h-4 w-4" />
              Im App Store laden
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackAndroid}
              data-testid="hero-cta-googleplay"
              className="btn-primary group flex items-center gap-2 px-7 py-3.5 text-sm font-medium"
            >
              <Play className="h-4 w-4" />
              Bei Google Play laden
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <button onClick={() => scrollTo("features")} data-testid="hero-cta-secondary" className="btn-ghost px-7 py-3.5 text-sm font-medium">
              Mehr erfahren
            </button>
          </motion.div>

        </div>

        {/* Two phones side by side: Übersicht (left) + Mitbringsel (right) */}
        <div className="relative mx-auto flex w-full max-w-[440px] items-center justify-center gap-3 sm:gap-6 lg:max-w-[520px]">
          <motion.div
            className="w-[150px] sm:w-[190px] lg:w-[232px]"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <PhoneMockup
              src="/generated/app_uebersicht.png"
              alt="FestivalPlan Übersicht"
              tilt={-9}
              sheen={false}
              island={false}
              testId="hero-phone"
            />
          </motion.div>

          <motion.div
            className="w-[150px] sm:w-[190px] lg:w-[232px]"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <PhoneMockup
              src="/generated/app_mitbringliste.png"
              alt="FestivalPlan Mitbringliste"
              glow="cyan"
              tilt={9}
              sheen={false}
              island={false}
              testId="hero-phone-secondary"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
